import * as React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/plugin/utc";
import "dayjs/plugin/timezone";

dayjs.extend(require("dayjs/plugin/utc"));
dayjs.extend(require("dayjs/plugin/timezone"));

interface CustomDateTimePickerProps {
  label?: string;
  value: dayjs.Dayjs | null;
  onTimeChange: (newTime: dayjs.Dayjs | null) => void;
  error?: boolean;
  minDate: any;
  disabled?: boolean;
  ampm?: boolean;
  isInputReadOnly?: boolean;
  shouldDisableDate?: (date: dayjs.Dayjs) => boolean;
  shouldDisableTime?: (value: dayjs.Dayjs, view: any) => boolean;
}

const CustomDateTimePicker = React.forwardRef<
  HTMLInputElement,
  CustomDateTimePickerProps
>(
  (
    {
      label,
      value,
      onTimeChange,
      error = false,
      minDate,
      disabled,
      ampm = true,
      isInputReadOnly = false,
      shouldDisableDate,
      shouldDisableTime,
    },
    ref,
  ) => {
    const displayFormat = ampm ? "DD/MM/YYYY hh:mm A" : "DD/MM/YYYY HH:mm";

    const [selectedTime, setSelectedTime] = React.useState<dayjs.Dayjs | null>(
      value && dayjs.isDayjs(value) ? value.tz("Asia/Kolkata", true) : null,
    );

    React.useEffect(() => {
      if (value) {
        setSelectedTime(dayjs.tz(value, "Asia/Kolkata"));
      }
    }, [value]);

    const handleTimeChange = (newValue: dayjs.Dayjs | null) => {
      const indiaTime = newValue ? newValue.tz("Asia/Kolkata", true) : null;
      setSelectedTime(indiaTime);
      onTimeChange(indiaTime);
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          label={label || undefined}
          value={selectedTime}
          minDate={minDate}
          disabled={disabled}
          onChange={handleTimeChange}
          format={displayFormat}
          {...(shouldDisableDate ? { shouldDisableDate } : {})}
          {...(shouldDisableTime ? { shouldDisableTime } : {})}
          slotProps={{
            textField: {
              size: "small",
              inputRef: ref,
              error: error,
              inputProps: {
                readOnly: isInputReadOnly,
              },
              onKeyDown: isInputReadOnly
                ? (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key !== "Tab") {
                      event.preventDefault();
                    }
                  }
                : undefined,
              onPaste: isInputReadOnly
                ? (event: React.ClipboardEvent<HTMLInputElement>) => {
                    event.preventDefault();
                  }
                : undefined,
              sx: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: disabled ? "#e1e1e1" : "#FAFBFD",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: disabled ? "none" : undefined,
                  },
                  borderColor: error ? "red" : undefined,
                  "&.Mui-focused fieldset": {
                    borderColor: error ? "red" : undefined,
                  },
                },
              },
            },
          }}
          ampm={ampm}
          disableOpenPicker={false}
        />
      </LocalizationProvider>
    );
  },
);

export default CustomDateTimePicker;
